import { Star, Clock, Users, TrendingUp, TrendingDown, Minus, Play } from "lucide-react";
import type { Combination, TrendDirection } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";
import { relativeTime } from "@/lib/utils/date";

interface CombinationCardProps {
  combination: Combination;
  rank?: number;
  trend?: TrendDirection;
  windowCount?: number;
  onApply: () => void;
  onFavorite?: () => void;
  isFavorited?: boolean;
  compact?: boolean;
}

const TREND_ICON = { up: TrendingUp, down: TrendingDown, stable: Minus };
const TREND_COLOR = {
  up: "text-success",
  down: "text-destructive",
  stable: "text-muted-foreground",
};

/**
 * Clickable combination tile used across Top 10, Trending, Favorites, Recents
 * and Recommendations. The whole card applies the combination (one-click state
 * restoration); the star toggles a favorite without triggering apply.
 */
export function CombinationCard({
  combination,
  rank,
  trend,
  windowCount,
  onApply,
  onFavorite,
  isFavorited,
  compact,
}: CombinationCardProps) {
  const TrendIcon = trend ? TREND_ICON[trend] : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onApply}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onApply();
        }
      }}
      className={cn(
        "group glass-sm flex items-center gap-3 p-2 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glass focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer",
      )}
    >
      {rank !== undefined && (
        <span
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
            rank <= 3 ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground",
          )}
        >
          {rank}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{combination.signature}</p>
        {!compact && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Play className="h-3 w-3" /> {combination.usageCount} uses
            </span>
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" /> {combination.uniqueUsers.length}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> {relativeTime(combination.lastUsedAt)}
            </span>
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {windowCount !== undefined && TrendIcon && (
          <Badge variant="outline" className="gap-1">
            <TrendIcon className={cn("h-3 w-3", trend && TREND_COLOR[trend])} />
            {windowCount}
          </Badge>
        )}
        {onFavorite && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 opacity-0 transition group-hover:opacity-100 data-[fav=true]:opacity-100"
            data-fav={isFavorited}
            aria-label={isFavorited ? "Remove favorite" : "Add favorite"}
            onClick={(e) => {
              e.stopPropagation();
              onFavorite();
            }}
          >
            <Star
              className={cn(
                "h-4 w-4",
                isFavorited ? "fill-warning text-warning" : "text-muted-foreground",
              )}
            />
          </Button>
        )}
      </div>
    </div>
  );
}
